import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { usePageMeta } from "@/lib/meta";
import { CITIES, SITE, waDefaultLink } from "@/lib/site";
import { WhatsAppIcon } from "@/components/Footer";
import DownloadProfileButton from "@/components/DownloadProfileButton";

const VALUES = [
  {
    title: "The render is the contract",
    desc: "Approve a 3D and the site is built against it. If the site drifts, the drift is discussed, not discovered.",
  },
  {
    title: "Materials chosen in person",
    desc: "Stone, veneer, brass, fabric — you touch the actual samples that go into your render and your home.",
  },
  {
    title: "One name, start to finish",
    desc: "Design, BOQ, execution and styling under one accountable studio. No contractor relay, no finger-pointing.",
  },
];

export default function About() {
  usePageMeta(
    "About Shriya Redkar — 19 Years of Turnkey Interiors | Shriya Redkar Interiors",
    "J.J. School of Art alumnus, 19 years of turnkey interior practice, 6 execution teams across 7 cities. Meet the studio behind the render-vs-reality proof."
  );

  return (
    <main className="pt-[76px]">
      <section className="container-x grid items-center gap-12 py-16 lg:grid-cols-12 lg:py-24">
        <Reveal className="lg:col-span-5">
          <div className="frame-clipped">
            <img
              src="/images/site/portrait.jpg"
              alt="Shriya Redkar in her Vashi studio"
              className="aspect-[3/4] w-full object-cover"
              data-testid="about-portrait"
            />
          </div>
        </Reveal>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <p className="eyebrow">About the studio</p>
            <h1 className="mt-5 font-serif text-4xl font-light leading-[1.08] text-zinc-50 sm:text-5xl lg:text-6xl">
              Nineteen years.
              <span className="block italic text-gold">One accountable name.</span>
            </h1>
            <p className="mt-8 text-base leading-relaxed text-zinc-400">
              Shriya Redkar trained at the Sir J.J. Institute of Applied Art and spent the last 19 years
              doing one thing well: full-home and commercial turnkey interiors, delivered across Mumbai,
              Navi Mumbai, Thane, Pune, Delhi, Lucknow and Rishikesh.
            </p>
            <p className="mt-5 text-base leading-relaxed text-zinc-400">
              The studio's promise is unusual because it's checkable: the 3D you approve is the home you
              get. Every delivered project is published as render beside reality — with honest captions
              about lighting, site surprises and change requests — because clients who can see the gap
              trust the gap-free parts.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/enquire" data-testid="about-cta-enquire" className="btn-gold">
                Work with the studio
              </Link>
              <a
                href={waDefaultLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-cta-whatsapp"
                className="btn-ghost"
              >
                <WhatsAppIcon className="h-4 w-4 text-wa" /> WhatsApp
              </a>
              <DownloadProfileButton testId="about-download-profile" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/10 bg-obsidian-soft">
        <div className="container-x grid grid-cols-2 gap-8 py-14 md:grid-cols-4" data-testid="about-trust-markers">
          {[
            { v: "19", k: "Years of turnkey practice" },
            { v: "J.J.", k: "School of Art alumnus" },
            { v: "6", k: "Execution teams" },
            { v: "7", k: "Cities served" },
          ].map((s) => (
            <div key={s.k}>
              <p className="font-serif text-4xl text-gold md:text-5xl">{s.v}</p>
              <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-zinc-400">{s.k}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x py-20 lg:py-28">
        <div className="grid gap-6 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="h-full border border-white/10 bg-obsidian-card p-8" data-testid={`about-value-${i}`}>
                <p className="font-mono text-xs text-gold">0{i + 1}</p>
                <h2 className="mt-4 font-serif text-2xl text-zinc-50">{v.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16">
            <p className="eyebrow">Cities</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {CITIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/interiors/${c.slug}`}
                  data-testid={`about-link-city-${c.slug}`}
                  className="border border-white/15 px-4 py-2 text-sm text-zinc-300 transition-colors hover:border-gold hover:text-gold"
                >
                  {c.name}
                </Link>
              ))}
            </div>
            <p className="mt-8 text-sm text-zinc-500">
              Studio: Vashi, Navi Mumbai · {SITE.phoneDisplay} · {SITE.instagramHandle} on Instagram
            </p>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
